/**
 * Brand kit — logo overlay + watermark text on the burned MP4 + live preview.
 *
 * Reusable contract that keeps the auto-captions pipeline thin: this file
 * owns the brand-kit state (logo blob URL + position + size + opacity, plus
 * watermark text + position + opacity), persists it to localStorage, and
 * exposes helpers for both:
 *
 *   • Live preview — absolutely-positioned <img> / <div> over the stage
 *     element so the user sees the brand placement before burning.
 *   • Burn-in — produces the FFmpeg `-filter_complex` graph fragment that
 *     composites the logo (PNG via overlay) and writes the watermark text
 *     (libass dialogue line). The pipeline glues this to the subtitle
 *     burn-in graph.
 *
 * Privacy contract: logo file lives only as a Blob URL in this tab. We
 * never upload the user's logo to any server; in localStorage we keep
 * just the position/size/opacity/text + a base64 thumbnail for quick
 * restore on reload (capped at 200 KB so we don't blow out localStorage).
 */
(function(){
    'use strict';

    var STORAGE_KEY = 'ac_brand_kit_v1';
    var THUMB_LIMIT_BYTES = 200 * 1024; // 200 KB cap — typical small SVG/PNG fits

    function readBrandKit(){
        try {
            var raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return defaults();
            var parsed = JSON.parse(raw);
            return Object.assign(defaults(), parsed);
        } catch(e){ return defaults(); }
    }
    function writeBrandKit(bk){
        // Don't persist the live Blob URL — it dies on reload anyway. Keep a
        // base64 dataUri snapshot if it fits the cap.
        var snapshot = Object.assign({}, bk);
        delete snapshot.logoBlobUrl;
        delete snapshot.logoFile;
        // Route through __safeStorageSet so QuotaExceededError gets a single
        // session toast + telemetry instead of being silently swallowed — was
        // the canonical "user uploaded a 4 MB logo, brand kit silently dropped
        // it, blamed the tool" failure mode.
        if (typeof window.__safeStorageSet === 'function') {
            window.__safeStorageSet(STORAGE_KEY, JSON.stringify(snapshot));
        } else {
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot)); } catch(e){}
        }
    }
    function defaults(){
        return {
            // Logo
            logoDataUri:    '',          // base64 thumbnail for persistence
            logoBlobUrl:    '',          // ephemeral live URL
            logoFile:       null,        // ephemeral File (for burn-in)
            logoPosition:   'tr',        // tl, tr, bl, br
            logoSize:       'md',        // sm, md, lg
            logoOpacity:    1.0,
            // Watermark
            watermarkText:  '',
            watermarkPosition: 'br',
            watermarkSize:  'md',
            watermarkOpacity: 0.85,
        };
    }

    /* Read a File → dataUri (small files only, capped at THUMB_LIMIT_BYTES). */
    function readDataUri(file){
        return new Promise(function(resolve, reject){
            if (!file) { resolve(''); return; }
            if (file.size > THUMB_LIMIT_BYTES * 8) {
                // File too big to store; return empty (will lose persistence
                // across reloads but live preview still works).
                resolve('');
                return;
            }
            var r = new FileReader();
            r.onload = function(){ resolve(String(r.result || '')); };
            r.onerror = function(){ reject(new Error('logo read failed')); };
            r.readAsDataURL(file);
        });
    }

    /* Build a {top,right,bottom,left} CSS shorthand for live preview. */
    var POS_CSS = {
        tl: { top: '6%',    left: '6%' },
        tr: { top: '6%',    right: '6%' },
        bl: { bottom: '6%', left: '6%' },
        br: { bottom: '6%', right: '6%' },
    };
    var SIZE_CSS = { sm: '8%', md: '12%', lg: '18%' };
    var SIZE_PX  = { sm: 80, md: 140, lg: 220 }; // approximate, used as ASS hint

    /* Render the brand kit overlay inside a parent element (the stage).
       Cleans up any prior overlay and rebuilds — cheap enough to call on
       every state change. */
    function renderPreview(stageEl, bk){
        if (!stageEl) return;
        var prior = stageEl.querySelector('.ac-brand-overlay');
        if (prior) prior.remove();
        if (!bk || (!bk.logoBlobUrl && !bk.logoDataUri && !bk.watermarkText)) return;

        var box = document.createElement('div');
        box.className = 'ac-brand-overlay';
        box.style.position = 'absolute';
        box.style.inset = '0';
        box.style.pointerEvents = 'none';
        box.style.zIndex = '5'; // above caption overlay (z=2-4) but below controls
        stageEl.appendChild(box);

        if (bk.logoBlobUrl || bk.logoDataUri) {
            var img = document.createElement('img');
            img.src = bk.logoBlobUrl || bk.logoDataUri;
            img.alt = '';
            img.style.position = 'absolute';
            img.style.width = SIZE_CSS[bk.logoSize] || SIZE_CSS.md;
            img.style.height = 'auto';
            img.style.opacity = String(bk.logoOpacity);
            img.style.objectFit = 'contain';
            img.style.pointerEvents = 'none';
            var pos = POS_CSS[bk.logoPosition] || POS_CSS.tr;
            for (var k in pos) img.style[k] = pos[k];
            box.appendChild(img);
        }

        if (bk.watermarkText) {
            var wm = document.createElement('div');
            wm.textContent = bk.watermarkText;
            wm.style.position = 'absolute';
            wm.style.color = '#FFFFFF';
            wm.style.opacity = String(bk.watermarkOpacity);
            wm.style.fontWeight = '600';
            wm.style.fontSize = ({ sm: '12px', md: '16px', lg: '20px' })[bk.watermarkSize] || '16px';
            wm.style.textShadow = '0 0 4px rgba(0,0,0,.7), 1px 1px 0 #000';
            wm.style.padding = '4px 8px';
            wm.style.background = 'rgba(0,0,0,.25)';
            wm.style.borderRadius = '4px';
            wm.style.pointerEvents = 'none';
            wm.style.maxWidth = '40%';
            wm.style.whiteSpace = 'nowrap';
            wm.style.overflow = 'hidden';
            wm.style.textOverflow = 'ellipsis';
            var wpos = POS_CSS[bk.watermarkPosition] || POS_CSS.br;
            for (var k2 in wpos) wm.style[k2] = wpos[k2];
            box.appendChild(wm);
        }
    }

    /* Plan an FFmpeg overlay graph fragment for burn-in. We only build the
       LOGO graph here; the watermark gets emitted as an extra ASS dialogue
       line which the existing libass burn-in will pick up.

       Returns:
         {
           extraInputs: [File, ...],            // files to add as -i sources
           filterChainPrefix: '[0:v]',          // input video tag for the next filter
           filterChainSegment: '...',           // graph appending logo, ending in [vbrand]
           outputTag: '[vbrand]',               // tag to feed into the subtitle filter
           assDialogue: '...'                   // ASS line for watermark text
         }

       When there is NO logo and NO watermark, returns null so the caller
       skips this entirely. */
    function planBurnIn(bk, baseVideoTag){
        if (!bk) return null;
        var hasLogo = !!bk.logoFile;
        var hasWm   = !!(bk.watermarkText && bk.watermarkText.trim());
        if (!hasLogo && !hasWm) return null;

        var plan = {
            extraInputs: [],
            filterChainSegment: '',
            outputTag: baseVideoTag,
            assDialogue: '',
        };

        if (hasLogo) {
            plan.extraInputs.push(bk.logoFile);
            // Calculate scale + position. Use 1080p target (PlayResY = 1080
            // in our ASS header) so position math matches.
            var heightFrac = ({ sm: 0.06, md: 0.10, lg: 0.16 })[bk.logoSize] || 0.10;
            var alpha = Math.max(0, Math.min(1, bk.logoOpacity || 1));
            // Position offsets — 4% margin
            var x, y;
            switch (bk.logoPosition || 'tr') {
                case 'tl': x = 'main_w*0.04';                  y = 'main_h*0.04'; break;
                case 'tr': x = 'main_w*0.96-overlay_w';        y = 'main_h*0.04'; break;
                case 'bl': x = 'main_w*0.04';                  y = 'main_h*0.96-overlay_h'; break;
                case 'br':
                default:   x = 'main_w*0.96-overlay_w';        y = 'main_h*0.96-overlay_h'; break;
            }
            // -2 keeps the aspect ratio
            var scale = '[1:v]scale=-2:main_h*' + heightFrac + ',format=rgba,colorchannelmixer=aa=' + alpha.toFixed(2) + '[brand_logo]';
            plan.filterChainSegment = scale + ';' + baseVideoTag + '[brand_logo]overlay=x=' + x + ':y=' + y + '[vbrand]';
            plan.outputTag = '[vbrand]';
        }

        if (hasWm) {
            // Watermark via libass dialogue line. Position by ASS alignment.
            // Use a simple {\an} alignment with a small margin.
            var wmAlign;
            switch (bk.watermarkPosition || 'br') {
                case 'tl': wmAlign = 7; break;
                case 'tr': wmAlign = 9; break;
                case 'bl': wmAlign = 1; break;
                case 'br':
                default:   wmAlign = 3; break;
            }
            // Size scaling — relative to PlayResY=1080 in ASS header
            var fsScale = ({ sm: 28, md: 38, lg: 52 })[bk.watermarkSize] || 38;
            var alphaWm = Math.max(0, Math.min(255, Math.round((1 - (bk.watermarkOpacity || 0.85)) * 255)));
            var hexA = alphaWm.toString(16).toUpperCase().padStart(2, '0');
            // Persist for the entire video duration — caller knows the duration
            // so we return a TEMPLATE that they substitute. The template uses
            // {{TOTAL_CS}} as a placeholder for the end timestamp in centiseconds.
            var escaped = String(bk.watermarkText)
                .replace(/\{/g, '(').replace(/\}/g, ')')
                .replace(/\n/g, '\\N');
            plan.assDialogue =
                'Dialogue: 0,0:00:00.00,{{ASS_END}},Default,,0,0,0,,{\\an' + wmAlign + '\\fs' + fsScale + '\\bord1\\shad0\\1a&H' + hexA + '&\\3a&H' + hexA + '&}' + escaped + '\n';
        }

        return plan;
    }

    window.__acBrandKit = {
        read: readBrandKit,
        write: writeBrandKit,
        defaults: defaults,
        readDataUri: readDataUri,
        renderPreview: renderPreview,
        planBurnIn: planBurnIn,
        SIZE_PX: SIZE_PX,
    };
})();
